import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, callApi, detailMessage } from '../api';
import InfoTip from '../components/InfoTip';
import SearchableSelect from '../components/SearchableSelect';
import StatTile from '../components/StatTile';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CardSkeleton, EmptyState, Icon, PageHeader, Pagination, SearchInput, Switch, Tabs,
  TableSkeleton, cx,
} from '../components/ui';

// ── Heartbeat: the platform check-in ────────────────────────────────────────
// On each run the platform picks a share of the active users and wakes each one's agent
// with a check-in; the agent writes only if it has something worth saying, otherwise it
// answers NO_REPLY and nothing is sent. The rules (score, eligibility, the draw, the
// timing) live in proxy/shared/checkins.py and are shared with proxy-crons, which plans
// and dispatches, so what this page shows is what the planner does.
//
// Layout rule: a switch acts the moment it is flipped, a form waits for Save. Armed and
// Dry run are switches in the status card and save on their own. Everything else is a
// form on the Settings tab, grouped by the question it answers, and kept only through the
// save bar, which follows the page while there is anything unsaved.

const TABS = ['overview', 'users', 'history', 'settings'];

// The settings the form edits. Armed and Dry run are not among them: they are switches.
const FORM_KEYS = ['every_days', 'percent_per_day', 'strategy', 'plan_hour', 'timezone',
  'windows', 'min_days_between', 'quiet_hours', 'max_idle_days', 'max_ignored'];
const NUMBER_KEYS = ['every_days', 'percent_per_day', 'min_days_between', 'quiet_hours',
  'max_idle_days', 'max_ignored'];
const NUMERIC = new Set([...NUMBER_KEYS, 'plan_hour']);

const STATE = {
  off: { label: 'Disarmed', tone: 'neutral',
    text: 'Nothing is planned and no agent is woken.' },
  dry: { label: 'Armed, dry run', tone: 'violet',
    text: 'Each run picks and times its check-ins and records them, without waking any agent.' },
  live: { label: 'Armed, live', tone: 'ok',
    text: 'Each run wakes the agents it picks at their check-in time. An agent writes only if it has something to say.' },
};

const STATUS = {
  planned: { tone: 'info', label: 'Planned' },
  dry_run: { tone: 'violet', label: 'Dry run' },
  dispatched: { tone: 'ok', label: 'Agent woken' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  failed: { tone: 'danger', label: 'Failed' },
};
const STATUS_ORDER = ['planned', 'dry_run', 'dispatched', 'skipped', 'failed'];

const PAGE_SIZE = 25;

// ── Formatting ──────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');
const hourText = (h) => `${pad2(h)}:00`;
const plural = (n, word, many) => `${n} ${n === 1 ? word : (many || `${word}s`)}`;
const sentence = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function formatWhen(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function formatTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// Plan days are platform-local dates ("2026-09-30"): read and shifted as UTC so the
// browser's own timezone cannot move them by a day.
function parseDay(day) {
  const [y, m, d] = String(day).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}
const addDays = (day, n) => new Date(parseDay(day) + n * 86400000).toISOString().slice(0, 10);
const daysBetween = (from, to) => Math.round((parseDay(to) - parseDay(from)) / 86400000);
function formatDay(day) {
  if (!day) return '-';
  return new Date(parseDay(day)).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

function formatDays(days) {
  if (days == null) return 'No message on record';
  if (days < 1) return `${Math.max(1, Math.round(days * 24))} h ago`;
  return `${days < 10 ? days.toFixed(1) : Math.round(days)} days ago`;
}

// Mirrors checkins.next_run_day, so the Settings tab can say what an unsaved cadence does.
function nextRunDay(today, lastRun, every) {
  if (!lastRun) return today;
  const since = daysBetween(lastRun, today);
  if (since === 0) return addDays(today, every);
  if (since >= every) return today;
  return addDays(lastRun, every);
}

// 5% of 41 users is 2.05, and the planner rounds the fraction at random: "2 or 3".
function perRun(active, pct) {
  const exact = (Number(active) || 0) * (Number(pct) || 0) / 100;
  const lo = Math.floor(exact);
  const hi = Math.ceil(exact);
  return lo === hi ? String(lo) : `${lo} or ${hi}`;
}

// ── Time windows: the stored form is "10-12,17-19" (checkins.parse_windows) ─────

function parseWindowList(value) {
  if (typeof value !== 'string' || !value.trim()) return [];
  const out = [];
  value.split(',').forEach((part) => {
    const m = /^\s*(\d{1,2})\s*-\s*(\d{1,2})\s*$/.exec(part);
    if (m) out.push({ start: Number(m[1]), end: Number(m[2]) });
  });
  return out;
}

const windowsText = (list) => list.map((w) => `${w.start}-${w.end}`).join(',');

function windowsProblem(list) {
  if (!list.length) return 'Add at least one window.';
  if (list.some((w) => !(w.start >= 0 && w.end <= 24 && w.start < w.end))) {
    return 'Each window has to end after it starts.';
  }
  const sorted = [...list].sort((a, b) => a.start - b.start);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].start < sorted[i - 1].end) return 'Windows must not overlap.';
  }
  return null;
}

function describeWindows(list) {
  const parts = [...list].sort((a, b) => a.start - b.start)
    .map((w) => `${hourText(w.start)} and ${hourText(w.end)}`);
  if (parts.length <= 1) return parts[0] ? `between ${parts[0]}` : 'at no time';
  return `between ${parts.slice(0, -1).join(', between ')} or between ${parts[parts.length - 1]}`;
}

// Where "Add a window" starts: the first free hour from 08:00, two hours long at most.
function newWindow(list) {
  const covered = (h) => list.some((w) => h >= w.start && h < w.end);
  const order = [...Array.from({ length: 16 }, (_, i) => i + 8), ...Array.from({ length: 8 }, (_, i) => i)];
  const start = order.find((h) => !covered(h));
  if (start == null) return null;
  const nextStart = Math.min(24, ...list.filter((w) => w.start > start).map((w) => w.start));
  return { start, end: Math.min(start + 2, nextStart) };
}

// ── Validation, mirroring checkins._coerce so a bad value is caught before Save ─

function numberProblem(field, raw) {
  const text = String(raw ?? '').trim();
  const n = Number(text);
  if (!text || !Number.isFinite(n)) return 'Enter a number.';
  if (field.kind === 'int' && !Number.isInteger(n)) return 'Use a whole number.';
  if (field.min != null && n < field.min) return `At least ${field.min}.`;
  if (field.max != null && n > field.max) return `At most ${field.max}.`;
  return null;
}

const FALLBACK_ZONES = ['UTC', 'Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Madrid', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Dubai',
  'Asia/Tokyo', 'Australia/Sydney'];
let zoneCache = null;
function zoneOptions(current) {
  if (!zoneCache) {
    let list = [];
    try {
      list = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    } catch (_) {
      list = [];
    }
    zoneCache = list.length ? ['UTC', ...list.filter((z) => z !== 'UTC')] : FALLBACK_ZONES;
  }
  const list = !current || zoneCache.includes(current) ? zoneCache : [current, ...zoneCache];
  return list.map((z) => ({ value: z, label: z.replace(/_/g, ' ') }));
}

function reasonTone(reason) {
  if (reason === 'opted out' || String(reason).startsWith('ignored')) return 'warn';
  if (reason === 'talking to the agent') return 'info';
  return 'neutral';
}

// A bar, not only a number: the table is read by comparing rows, and a bar makes the
// top of the draw visible at a glance.
function ScoreBar({ value, max = 1.2 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <span className="row gap-2 nowrap">
      <span className="hb-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
      <span className="num">{value.toFixed(2)}</span>
    </span>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────

function PreviewResult({ preview, onClose }) {
  return (
    <div className="card-inset mt-4" role="status">
      <div className="row between gap-3 mb-2">
        <span className="overline">
          A draw with the saved settings: {preview.picks.length} of {preview.eligible} eligible
        </span>
        <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label="Close the preview"
                onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      {preview.picks.length === 0 ? (
        <p className="text-muted m-0">
          Nobody would be checked in on today
          {preview.budget > 0 ? ': every window has passed, or nobody is eligible.' : '.'}
        </p>
      ) : (
        <ol className="hb-today">
          {preview.picks.map((p) => (
            <li key={p.user_id} className="hb-today-row">
              <span className="num hb-today-time">{formatTime(p.planned_for)}</span>
              <span className="grow min-w-0"><Link to={`/users/${p.user_id}`}>{p.username}</Link></span>
              <span className="text-sm text-muted num">score {p.score.toFixed(2)}</span>
            </li>
          ))}
        </ol>
      )}
      <p className="hb-sub mt-2 mb-0">Nothing is saved, and each preview draws again.</p>
    </div>
  );
}

function OverviewPanel({
  overview, users, checkins, isSuper, busy, preview, onPreview, onClosePreview, onReplan,
  onOpenSettings,
}) {
  const { settings, today } = overview;
  const week = overview.last_7_days || {};
  const active = users ? users.length : (today.active ?? 0);
  const eligible = users ? users.filter((u) => u.eligible).length : null;
  const windows = parseWindowList(settings.windows);
  const strategy = ((overview.fields || []).find((f) => f.key === 'strategy')?.choices || [])
    .find((c) => c.value === settings.strategy);
  const every = Number(settings.every_days) || 1;
  const todays = useMemo(() => (checkins || [])
    .filter((c) => c.plan_day === today.day)
    .sort((a, b) => String(a.planned_for).localeCompare(String(b.planned_for))), [checkins, today.day]);

  let todayValue;
  let todaySub;
  let emptyText;
  if (today.planned) {
    todayValue = today.picked;
    todaySub = `planned, of ${today.eligible} eligible`;
    emptyText = today.picked ? 'No check-in of today is on record yet.' : 'The run found nobody to check in on.';
  } else if (!settings.enabled) {
    todayValue = 'Off';
    todaySub = 'nothing is planned while disarmed';
    emptyText = 'Nothing is planned while check-ins are disarmed.';
  } else if (!today.run_day) {
    todayValue = 'No run';
    todaySub = `next run ${formatDay(today.next_run)}`;
    emptyText = `No run today. The next run is on ${formatDay(today.next_run)}.`;
  } else {
    todayValue = 'Pending';
    todaySub = today.plan_due ? 'being planned now' : `planned at ${hourText(settings.plan_hour)}`;
    emptyText = today.plan_due
      ? "Today's users are being picked now. Refresh in a couple of minutes."
      : `Today's users are picked at ${hourText(settings.plan_hour)} ${settings.timezone}.`;
  }

  return (
    <>
      <div className="hb-tiles">
        <StatTile label="Today's run" value={todayValue} sub={todaySub} icon="calendar" />
        <StatTile label="Eligible now" value={eligible ?? '-'} sub={`of ${plural(active, 'active user')}`} icon="users" />
        <StatTile label="Woken, last 7 days" value={week.dispatched || 0} icon="activity"
                  sub={`${week.wrote || 0} wrote, ${week.replied || 0} answered${week.dry_run ? `, ${plural(week.dry_run, 'dry run')}` : ''}`} />
        <StatTile label="Opted out" value={overview.opted_out ?? 0} sub="never checked in on" icon="bell" />
      </div>

      <section className="card" aria-labelledby="hb-today-title">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="hb-today-title" className="card-title">Today, {formatDay(today.day)}</h2>
            <p className="card-subtitle">
              {today.planned
                ? `${plural(today.picked, 'check-in')} picked at ${formatTime(today.planned_at)}, from ${today.eligible} eligible of ${plural(today.active, 'active user')}.`
                : emptyText}
            </p>
          </div>
          {isSuper && (
            <div className="card-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={onPreview}>
                {busy === 'preview' ? <span className="spinner-sm" /> : <Icon name="eye" />}
                Preview a draw
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy || !today.planned}
                      onClick={onReplan}>
                {busy === 'replan' ? <span className="spinner-sm" /> : <Icon name="refresh" />}
                Replan today
              </button>
            </div>
          )}
        </div>
        {todays.length === 0 ? (
          <p className="empty-inline m-0">{today.planned ? emptyText : 'No check-ins today.'}</p>
        ) : (
          <ol className="hb-today">
            {todays.map((c) => {
              const st = STATUS[c.status] || { tone: 'neutral', label: c.status };
              return (
                <li key={c.id} className="hb-today-row">
                  <span className="num hb-today-time">{formatTime(c.dispatched_at || c.planned_for)}</span>
                  <span className="grow min-w-0">
                    {c.username ? <Link to={`/users/${c.user_id}`}>{c.username}</Link> : `#${c.user_id}`}
                    {c.reason && <span className="hb-sub">{sentence(c.reason)}</span>}
                  </span>
                  <Badge tone={st.tone}>{st.label}</Badge>
                </li>
              );
            })}
          </ol>
        )}
        {preview && <PreviewResult preview={preview} onClose={onClosePreview} />}
      </section>

      <section className="card" aria-labelledby="hb-setup-title">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="hb-setup-title" className="card-title">How it runs</h2>
            <p className="card-subtitle">The saved settings, in plain words.</p>
          </div>
          <div className="card-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenSettings}>
              <Icon name="sliders" /> {isSuper ? 'Change settings' : 'See the settings'}
            </button>
          </div>
        </div>
        <ul className="hb-summary">
          <li>
            <Icon name="calendar" />
            <span>
              <strong>{every === 1 ? 'Every day' : `Every ${every} days`}</strong>, at{' '}
              <strong>{hourText(settings.plan_hour)} {settings.timezone}</strong>, a run picks its
              users and times each check-in.
            </span>
          </li>
          <li>
            <Icon name="users" />
            <span>
              Each run checks in on <strong>{settings.percent_per_day}%</strong> of the active
              users: {perRun(active, settings.percent_per_day)} of the {active} there are now.
              Priority: <strong>{strategy?.label || settings.strategy}</strong>.
            </span>
          </li>
          <li>
            <Icon name="clock" />
            <span>
              A check-in happens <strong>{describeWindows(windows)}</strong>, in each user&apos;s
              own time.
            </span>
          </li>
          <li>
            <Icon name="shield" />
            <span>
              A user gets at most one every <strong>{plural(settings.min_days_between, 'day')}</strong>,
              none if they wrote in the last <strong>{plural(settings.quiet_hours, 'hour')}</strong> or
              have been silent over <strong>{plural(settings.max_idle_days, 'day')}</strong>, and none
              after <strong>{settings.max_ignored}</strong> unanswered in a row.
            </span>
          </li>
        </ul>
      </section>
    </>
  );
}

// ── Users ───────────────────────────────────────────────────────────────────

function UsersPanel({ users, overview, isSuper, busy, onOptOut }) {
  const [filter, setFilter] = useState('eligible');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { settings } = overview;
  const steps = overview.weights?.freshness || [];

  const counts = useMemo(() => {
    const list = users || [];
    return {
      eligible: list.filter((u) => u.eligible).length,
      blocked: list.filter((u) => !u.eligible && !u.opted_out).length,
      opted_out: list.filter((u) => u.opted_out).length,
      all: list.length,
    };
  }, [users]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((u) => {
      const inFilter = filter === 'all'
        || (filter === 'eligible' && u.eligible)
        || (filter === 'blocked' && !u.eligible && !u.opted_out)
        || (filter === 'opted_out' && u.opted_out);
      return inFilter && (!q || `${u.username} ${u.tier || ''} ${u.reason || ''}`.toLowerCase().includes(q));
    });
  }, [users, filter, search]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const shown = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pick = (v) => { setFilter(v); setPage(1); };

  return (
    <section className="card card-flush" aria-labelledby="hb-users-title">
      <div className="card-header">
        <div className="min-w-0">
          <h2 id="hb-users-title" className="card-title">Users and their scores</h2>
          <p className="card-subtitle">
            Eligible first, highest score first. The score only matters to the priorities that
            use it.
          </p>
        </div>
      </div>
      <div className="hb-legend">
        <span>Score = freshness × engagement × boost. Freshness, by when they last wrote:</span>
        <Badge tone="outline">under {settings.quiet_hours} h: 0</Badge>
        {steps.map((s, i) => (
          <Badge key={s.under_days} tone="outline">
            {i === steps.length - 1 ? 'up to' : 'under'} {s.under_days} days: {s.weight}
          </Badge>
        ))}
        <Badge tone="outline">later: 0</Badge>
      </div>
      <div className="toolbar">
        <Tabs size="sm" label="Show users" value={filter} onChange={pick} tabs={[
          { value: 'eligible', label: 'Eligible', count: counts.eligible },
          { value: 'blocked', label: 'Not eligible', count: counts.blocked },
          { value: 'opted_out', label: 'Opted out', count: counts.opted_out },
          { value: 'all', label: 'All', count: counts.all },
        ]} />
        <div className="toolbar-spacer" />
        <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
                     placeholder="Search users" label="Search users" />
      </div>
      {!users ? <TableSkeleton rows={6} cols={8} /> : rows.length === 0 ? (
        <EmptyState icon="users" title={search ? 'No user matches' : 'Nobody here'}
                    action={filter !== 'all' && (
                      <button type="button" className="btn btn-secondary" onClick={() => pick('all')}>
                        Show all users
                      </button>
                    )}>
          {filter === 'eligible' && !search
            ? 'Nobody can be checked in on right now. All users shows why each one is left out.'
            : 'Try another filter or search.'}
        </EmptyState>
      ) : (
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Last wrote</th>
                <th scope="col">Freshness</th>
                <th scope="col" className="text-right">Messages, 7 days</th>
                <th scope="col">Score</th>
                <th scope="col">Status</th>
                <th scope="col">Last check-in</th>
                <th scope="col">Check-ins</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.user_id}>
                  <td data-label="User">
                    <div className="cell-primary"><Link to={`/users/${u.user_id}`}>{u.username}</Link></div>
                    <span className="cell-sub">{u.tier || 'No tier'} · {u.timezone}</span>
                  </td>
                  <td data-label="Last wrote" className="nowrap">{formatDays(u.days_since_message)}</td>
                  <td data-label="Freshness"><ScoreBar value={u.freshness} max={1} /></td>
                  <td data-label="Messages, 7 days" className="num text-right">{u.inbound_7d}</td>
                  <td data-label="Score">
                    <ScoreBar value={u.score} />
                    {/* Only for the eligible: a left-out user scores 0 whatever the factors
                        say, and a product next to a 0 would read as a sum that is wrong. */}
                    {u.eligible && (
                      <span className="cell-sub nowrap">
                        {u.freshness.toFixed(1)} × {u.engagement.toFixed(2)} × {u.boost.toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td data-label="Status">
                    {u.eligible ? <Badge tone="ok" dot>Eligible</Badge>
                      : <Badge tone={reasonTone(u.reason)}>{sentence(u.reason || 'not eligible')}</Badge>}
                  </td>
                  <td data-label="Last check-in" className="nowrap">{formatWhen(u.last_checkin_at)}</td>
                  <td data-label="Check-ins">
                    <Switch id={`hb-opt-${u.user_id}`} checked={!u.opted_out}
                            disabled={!isSuper || !!busy}
                            onChange={(v) => onOptOut(u, !v)}
                            label={u.opted_out ? 'Opted out' : 'Allowed'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={safePage} pageSize={PAGE_SIZE} total={rows.length} onPage={setPage} noun="users" />
    </section>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

function wroteCell(c) {
  if (c.status !== 'dispatched') return <span className="text-faint">-</span>;
  if (c.wrote == null) return <Badge tone="neutral">Not known yet</Badge>;
  return c.wrote ? <Badge tone="ok">Yes</Badge> : <Badge tone="outline">No</Badge>;
}

function HistoryPanel({ checkins, settings }) {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  const counts = useMemo(() => {
    const out = {};
    (checkins || []).forEach((c) => { out[c.status] = (out[c.status] || 0) + 1; });
    return out;
  }, [checkins]);
  const rows = useMemo(() => (checkins || []).filter((c) => filter === 'all' || c.status === filter),
    [checkins, filter]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const shown = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const tabs = [
    { value: 'all', label: 'All', count: (checkins || []).length },
    ...STATUS_ORDER.filter((s) => counts[s]).map((s) => ({ value: s, label: STATUS[s].label, count: counts[s] })),
  ];

  return (
    <section className="card card-flush" aria-labelledby="hb-history-title">
      <div className="card-header">
        <div className="min-w-0">
          <h2 id="hb-history-title" className="card-title">Check-in history</h2>
          <p className="card-subtitle">
            The last 100. Agent wrote: it sent its user a message within the hour. Answered:
            the user wrote back within three days.
          </p>
        </div>
      </div>
      {!checkins ? <TableSkeleton rows={5} cols={6} /> : checkins.length === 0 ? (
        <EmptyState icon="clock" title="No check-ins yet">
          {settings.enabled ? 'The first ones appear once a run is planned.' : 'Arm check-ins to plan the first run.'}
        </EmptyState>
      ) : (
        <>
          <div className="toolbar">
            <Tabs size="sm" label="Show check-ins" value={filter} tabs={tabs}
                  onChange={(v) => { setFilter(v); setPage(1); }} />
          </div>
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">User</th>
                  <th scope="col">Result</th>
                  <th scope="col" className="text-right">Score</th>
                  <th scope="col">Agent wrote</th>
                  <th scope="col">Answered</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const st = STATUS[c.status] || { tone: 'neutral', label: c.status };
                  return (
                    <tr key={c.id}>
                      <td data-label="When" className="nowrap">{formatWhen(c.dispatched_at || c.planned_for)}</td>
                      <td data-label="User">
                        {c.username ? <Link to={`/users/${c.user_id}`}>{c.username}</Link> : `#${c.user_id}`}
                      </td>
                      <td data-label="Result">
                        <Badge tone={st.tone}>{st.label}</Badge>
                        {c.reason && <span className="cell-sub">{sentence(c.reason)}</span>}
                      </td>
                      <td data-label="Score" className="num text-right">{c.score != null ? c.score.toFixed(2) : '-'}</td>
                      <td data-label="Agent wrote">{wroteCell(c)}</td>
                      <td data-label="Answered" className="nowrap">{c.replied_at ? formatWhen(c.replied_at) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={safePage} pageSize={PAGE_SIZE} total={rows.length} onPage={setPage} noun="check-ins" />
        </>
      )}
    </section>
  );
}

// ── Settings ────────────────────────────────────────────────────────────────

// Label and explanation on the left, the control on the right; `stack` puts a wide control
// (the priority cards, the windows) under its label instead.
function SettingRow({ id, label, help, changed, error, readout, stack, labelFor = true, children }) {
  const titleProps = { className: 'hb-setting-title', id: `${id}-label` };
  return (
    <div className={cx('hb-setting', stack && 'hb-setting-stack')}>
      <div className="min-w-0">
        {labelFor
          ? <label htmlFor={id} {...titleProps}>{label}{changed && <Badge tone="warn">Unsaved</Badge>}</label>
          : <span {...titleProps}>{label}{changed && <Badge tone="warn">Unsaved</Badge>}</span>}
        {help && <p className="setting-row-desc mb-0" id={`${id}-desc`}>{help}</p>}
      </div>
      <div className="min-w-0">
        {children}
        {error
          ? <div className="form-error" id={`${id}-error`} role="alert">{error}</div>
          : readout && <div className="hb-setting-readout" aria-live="polite">{readout}</div>}
      </div>
    </div>
  );
}

function DayStrip({ windows, planHour }) {
  const pct = (h) => `${(h / 24) * 100}%`;
  return (
    <div className="hb-day" role="img"
         aria-label={`Check-ins happen ${describeWindows(windows)}; users are picked at ${hourText(planHour)}`}>
      <div className="hb-day-track">
        {windows.filter((w) => w.start < w.end).map((w) => (
          <span key={`${w.start}-${w.end}`} className="hb-day-window"
                style={{ left: pct(w.start), width: pct(w.end - w.start) }} />
        ))}
        <span className="hb-day-mark" style={{ left: pct(planHour) }} />
      </div>
      <div className="hb-day-ticks" aria-hidden="true">
        {[0, 6, 12, 18, 24].map((h) => <span key={h} style={{ left: pct(h) }}>{hourText(h)}</span>)}
      </div>
      <div className="hb-day-legend" aria-hidden="true">
        <span><span className="hb-key hb-key-window" />Check-in windows</span>
        <span><span className="hb-key hb-key-mark" />Users picked</span>
      </div>
    </div>
  );
}

function WindowsEditor({ value, onChange, disabled, planHour, describedBy }) {
  const list = parseWindowList(value);
  const update = (i, patch) => onChange(windowsText(list.map((w, j) => (j === i ? { ...w, ...patch } : w))));
  const remove = (i) => onChange(windowsText(list.filter((_, j) => j !== i)));
  const next = newWindow(list);
  const hours = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
  return (
    <div className="stack gap-3">
      <DayStrip windows={list} planHour={planHour} />
      <ul className="hb-windows" aria-describedby={describedBy}>
        {list.map((w, i) => (
          // Index keys on purpose: a window has no identity beyond its place in the list,
          // and both of its controls are fully controlled.
          <li key={i} className="hb-window">
            <select className="form-input" aria-label={`Window ${i + 1}, from`} value={w.start}
                    disabled={disabled} onChange={(e) => update(i, { start: Number(e.target.value) })}>
              {hours(0, 23).map((h) => <option key={h} value={h}>{hourText(h)}</option>)}
            </select>
            <span className="text-muted">to</span>
            <select className="form-input" aria-label={`Window ${i + 1}, to`} value={w.end}
                    disabled={disabled} onChange={(e) => update(i, { end: Number(e.target.value) })}>
              {hours(1, 24).map((h) => <option key={h} value={h}>{hourText(h)}</option>)}
            </select>
            <button type="button" className="btn btn-ghost btn-sm btn-icon"
                    aria-label={`Remove window ${i + 1}`} disabled={disabled || list.length <= 1}
                    onClick={() => remove(i)}>
              <Icon name="trash" />
            </button>
          </li>
        ))}
      </ul>
      <div>
        <button type="button" className="btn btn-secondary btn-sm" disabled={disabled || !next}
                onClick={() => onChange(windowsText([...list, next]))}>
          <Icon name="plus" /> Add a window
        </button>
      </div>
    </div>
  );
}

function SettingsPanel({ form, set, fields, changes, problems, disabled, isSuper, overview, users }) {
  const f = (k) => fields[k] || {};
  const today = overview.today;
  const windows = parseWindowList(form.windows);
  const every = Number(form.every_days);
  const next = Number.isInteger(every) && every >= 1 ? nextRunDay(today.day, today.last_run, every) : null;
  const active = users ? users.length : (today.active ?? 0);
  const eligible = users ? users.filter((u) => u.eligible).length : null;
  const pct = Number(form.percent_per_day);
  const lastEnd = windows.length ? Math.max(...windows.map((w) => w.end)) : null;
  const planLate = lastEnd != null && Number(form.plan_hour) >= lastEnd;
  const tzOptions = useMemo(() => zoneOptions(form.timezone), [form.timezone]);

  const numberRow = (key, readout) => {
    const field = f(key);
    const id = `hb-${key}`;
    const error = problems[key];
    return (
      <SettingRow key={key} id={id} label={field.label} help={field.help} changed={key in changes}
                  error={error} readout={readout}>
        <div className="hb-num">
          <input id={id} type="number" inputMode="decimal"
                 className={cx('form-input num', key in changes && 'is-dirty')}
                 min={field.min ?? undefined} max={field.max ?? undefined}
                 step={field.kind === 'float' ? 'any' : 1}
                 value={form[key] ?? ''} disabled={disabled}
                 aria-invalid={error ? true : undefined}
                 aria-describedby={cx(`${id}-desc`, field.unit && `${id}-unit`, error && `${id}-error`)}
                 onChange={(e) => set(key, e.target.value)} />
          {field.unit && <span className="hb-unit" id={`${id}-unit`}>{field.unit}</span>}
        </div>
      </SettingRow>
    );
  };

  let everyReadout = null;
  if (next) {
    everyReadout = `${every === 1 ? 'Every day' : `Every ${every} days`}. `
      + (next === today.day ? 'The next run is today.' : `The next run is on ${formatDay(next)}.`);
  }
  let pctReadout = null;
  if (Number.isFinite(pct) && !problems.percent_per_day) {
    pctReadout = `About ${perRun(active, pct)} of the ${plural(active, 'active user')} per run.`
      + (eligible != null ? ` ${eligible} ${eligible === 1 ? 'is' : 'are'} eligible now, so a run picks at most ${eligible}.` : '');
  }

  return (
    <>
      {!isSuper && (
        <div className="notice notice-info" role="status">
          <Icon name="lock" />
          <div className="notice-body">Read only: a superadmin can change these.</div>
        </div>
      )}

      <section className="card" aria-labelledby="hb-set-runs">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="hb-set-runs" className="card-title">Runs</h2>
            <p className="card-subtitle">How often the platform checks in, on how many users, and who goes first.</p>
          </div>
        </div>
        {numberRow('every_days', everyReadout)}
        {numberRow('percent_per_day', pctReadout)}
        <SettingRow id="hb-strategy" label={f('strategy').label} help={f('strategy').help}
                    changed={'strategy' in changes} stack labelFor={false}>
          <div role="radiogroup" aria-labelledby="hb-strategy-label" aria-describedby="hb-strategy-desc"
               className="hb-choices">
            {(f('strategy').choices || []).map((c) => (
              <label key={c.value} className={cx('check-row card-inset hb-choice', form.strategy === c.value && 'is-selected')}>
                <input type="radio" name="hb-strategy" value={c.value} checked={form.strategy === c.value}
                       disabled={disabled} onChange={() => set('strategy', c.value)} />
                <span>
                  <span className="fw-500">{c.label}</span>
                  <span className="check-hint">{c.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </SettingRow>
      </section>

      <section className="card" aria-labelledby="hb-set-timing">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="hb-set-timing" className="card-title">Timing</h2>
            <p className="card-subtitle">When a run picks its users, and when the check-ins happen.</p>
          </div>
        </div>
        <SettingRow id="hb-plan_hour" label={f('plan_hour').label} help={f('plan_hour').help}
                    changed={'plan_hour' in changes}>
          <select id="hb-plan_hour" className={cx('form-input hb-select', 'plan_hour' in changes && 'is-dirty')}
                  value={form.plan_hour} disabled={disabled} aria-describedby="hb-plan_hour-desc"
                  onChange={(e) => set('plan_hour', Number(e.target.value))}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourText(h)}</option>)}
          </select>
        </SettingRow>
        <SettingRow id="hb-timezone" label={f('timezone').label} help={f('timezone').help}
                    changed={'timezone' in changes} error={problems.timezone} labelFor={false}>
          <SearchableSelect id="hb-timezone" value={form.timezone || ''} options={tzOptions}
                            onChange={(v) => set('timezone', v)} placeholder="Pick a timezone"
                            allLabel={null} searchPlaceholder="Find a timezone" minWidth={220}
                            aria-labelledby="hb-timezone-label" aria-describedby="hb-timezone-desc"
                            disabled={disabled} />
        </SettingRow>
        <SettingRow id="hb-windows" label={f('windows').label} help={f('windows').help}
                    changed={'windows' in changes} error={problems.windows} stack labelFor={false}>
          <WindowsEditor value={form.windows} onChange={(v) => set('windows', v)} disabled={disabled}
                         planHour={Number(form.plan_hour) || 0} describedBy="hb-windows-desc" />
          {planLate && !problems.windows && (
            <div className="notice notice-warn mt-3 mb-0" role="status">
              <Icon name="alert" />
              <div className="notice-body">
                Users are picked at {hourText(form.plan_hour)}, when the last window has already
                closed: in the platform&apos;s timezone a run would find no time left to check in.
              </div>
            </div>
          )}
        </SettingRow>
      </section>

      <section className="card" aria-labelledby="hb-set-limits">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="hb-set-limits" className="card-title">Limits per user</h2>
            <p className="card-subtitle">Who a run leaves out, whatever their score.</p>
          </div>
        </div>
        {numberRow('min_days_between')}
        {numberRow('quiet_hours')}
        {numberRow('max_idle_days')}
        {numberRow('max_ignored')}
      </section>
    </>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

function Heartbeat() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'overview';
  const setTab = useCallback((value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'overview') next.delete('tab'); else next.set('tab', value);
      return next;
    }, { replace: true });
  }, [setParams]);

  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [checkins, setCheckins] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';

  const settings = overview?.settings;
  const fields = useMemo(
    () => Object.fromEntries((overview?.fields || []).map((f) => [f.key, f])), [overview]);

  const changes = useMemo(() => {
    if (!settings || !form) return {};
    const out = {};
    FORM_KEYS.forEach((k) => {
      const now = form[k];
      const same = NUMERIC.has(k)
        ? String(now ?? '').trim() !== '' && Number(now) === Number(settings[k])
        : String(now ?? '') === String(settings[k] ?? '');
      if (!same) out[k] = now;
    });
    return out;
  }, [form, settings]);
  // Read by `load`, so a refresh keeps an edit not saved yet and takes the rest fresh.
  const changesRef = useRef(changes);
  changesRef.current = changes;
  const changeCount = Object.keys(changes).length;
  const dirty = changeCount > 0;

  const problems = useMemo(() => {
    if (!form || !overview) return {};
    const out = {};
    NUMBER_KEYS.forEach((k) => {
      const p = numberProblem(fields[k] || {}, form[k]);
      if (p) out[k] = p;
    });
    const w = windowsProblem(parseWindowList(form.windows));
    if (w) out.windows = w;
    if (!form.timezone) out.timezone = 'Pick a timezone.';
    return out;
  }, [form, fields, overview]);
  const invalid = Object.keys(problems).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, u, c] = await Promise.all([
        apiFetch('/api/heartbeat'), apiFetch('/api/heartbeat/users'),
        apiFetch('/api/heartbeat/checkins?limit=100'),
      ]);
      if (!o.ok) {
        const err = await o.json().catch(() => ({}));
        throw new Error(detailMessage(err.detail) || 'Failed to load the check-in settings');
      }
      const ov = await o.json();
      setOverview(ov);
      setForm({ ...ov.settings, ...changesRef.current });
      setUsers(u.ok ? (await u.json()).users : []);
      setCheckins(c.ok ? (await c.json()).checkins : []);
      setLoadedAt(new Date());
    } catch (e) {
      toast.error(e.message || 'Failed to load the Heartbeat page');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Closing the tab or reloading with an edit not saved asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const put = (body, successMsg, errorMsg) => callApi('/api/heartbeat/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, { successMsg, errorMsg });

  // A switch saves on its own and leaves any edit in the form alone.
  const saveSwitch = async (key, next) => {
    if (key === 'enabled' && next && !settings.dry_run && !await confirm({
      title: 'Arm check-ins, live?',
      body: <p>Dry run is off, so agents will be woken and may write to their users on the next run.</p>,
      confirmLabel: 'Arm',
    })) return;
    if (key === 'dry_run' && !next && settings.enabled && !await confirm({
      title: 'Leave dry run?',
      body: (
        <p>
          From the next check-in due, agents are woken for real and may write to their
          users. Today&apos;s remaining planned check-ins go out live.
        </p>
      ),
      confirmLabel: 'Go live',
    })) return;
    setBusy(key);
    const message = key === 'enabled'
      ? (next ? 'Check-ins armed' : 'Check-ins disarmed')
      : (next ? 'Dry run on' : 'Dry run off: check-ins go out live');
    const data = await put({ [key]: next }, message, 'Failed to save');
    setBusy(null);
    if (data) {
      setOverview((o) => ({ ...o, settings: data }));
      setForm({ ...data, ...changesRef.current });
    }
  };

  const saveSettings = async () => {
    if (!dirty || invalid) return;
    setBusy('save');
    const body = Object.fromEntries(Object.entries(changes)
      .map(([k, v]) => [k, NUMERIC.has(k) ? Number(v) : v]));
    const data = await put(body, 'Settings saved', 'Failed to save the settings');
    setBusy(null);
    if (data) {
      changesRef.current = {};
      setOverview((o) => ({ ...o, settings: data }));
      setForm(data);
      load();    // eligibility and today's line depend on the settings
    }
  };

  const discard = () => setForm(settings);
  const set = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  const runPreview = async () => {
    setBusy('preview');
    const data = await callApi('/api/heartbeat/preview', { method: 'POST' },
      { errorMsg: 'Failed to preview the day' });
    setBusy(null);
    if (data) setPreview(data);
  };

  const replan = async () => {
    if (!await confirm({
      title: 'Replan today?',
      body: (
        <p>
          Today&apos;s check-ins not yet due are dropped, and the planner picks again with
          the saved settings within a couple of minutes.
        </p>
      ),
      confirmLabel: 'Replan',
      tone: 'primary',
    })) return;
    setBusy('replan');
    const data = await callApi('/api/heartbeat/replan', { method: 'POST' },
      { successMsg: 'Today will be planned again shortly', errorMsg: 'Failed to replan' });
    setBusy(null);
    if (data) load();
  };

  const setOptOut = async (user, optOut) => {
    setBusy(`opt-${user.user_id}`);
    const data = await callApi(`/api/heartbeat/users/${user.user_id}/opt-out`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ opt_out: optOut }),
    }, { successMsg: optOut ? 'No more check-ins for this user' : 'Check-ins allowed again',
         errorMsg: 'Failed to update the opt-out' });
    setBusy(null);
    if (data) load();
  };

  const header = (
    <PageHeader
      description="On each run the platform wakes a share of the agents so they can check in on their users. An agent writes only if it has something worth saying."
      meta={loadedAt && (
        <div className="page-meta"><span>Updated {loadedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span></div>
      )}
      actions={(
        <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading && overview ? <span className="spinner-sm" /> : <Icon name="refresh" />}
          Refresh
        </button>
      )}
    >
      <h1 className="page-title">
        Heartbeat
        <InfoTip label="About check-ins" width={380}>
          <p>
            A user&apos;s score is freshness (how recently they wrote) times engagement (how
            much they wrote this week) times a boost that grows with the time since their
            last check-in.
          </p>
          <p>
            Each run picks by the priority you choose and times each check-in inside a
            window, in the user&apos;s own time. An agent with nothing to say stays silent.
          </p>
        </InfoTip>
      </h1>
    </PageHeader>
  );

  if (!overview || !form) {
    return (
      <div>
        {header}
        {loading ? <><CardSkeleton lines={4} /><CardSkeleton lines={6} /></> : (
          <div className="notice notice-danger" role="alert">
            <Icon name="alertCircle" />
            <div className="notice-body">
              <div className="notice-title">The check-in settings did not load</div>
              Nothing can be changed until they can be read.
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
              <Icon name="refresh" /> Try again
            </button>
          </div>
        )}
        {confirmDialog}
      </div>
    );
  }

  const today = overview.today;
  const mode = !settings.enabled ? 'off' : settings.dry_run ? 'dry' : 'live';
  const state = STATE[mode];
  let runLine;
  if (!settings.enabled) {
    runLine = today.last_run ? `The last run was on ${formatDay(today.last_run)}.` : 'No run yet.';
  } else if (today.planned) {
    runLine = `Today's run picked ${plural(today.picked, 'user')} of ${today.eligible} eligible at ${formatTime(today.planned_at)}.`;
  } else if (!today.run_day) {
    runLine = `No run today. The next run is on ${formatDay(today.next_run)}, at ${hourText(settings.plan_hour)} ${settings.timezone}.`;
  } else if (today.plan_due) {
    runLine = "Today's run is picking its users now.";
  } else {
    runLine = `Today's run picks its users at ${hourText(settings.plan_hour)} ${settings.timezone}.`;
  }

  const tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'users', label: 'Users', count: users ? users.length : undefined },
    { value: 'history', label: 'History', count: checkins ? checkins.length : undefined },
    {
      value: 'settings',
      label: dirty ? (
        <>
          Settings
          <span className="hb-dirty-dot" aria-hidden="true" />
          <span className="sr-only"> (unsaved changes)</span>
        </>
      ) : 'Settings',
    },
  ];

  return (
    <div>
      {header}

      <section className="card hb-status" aria-labelledby="hb-status-title">
        <div className="min-w-0">
          <div className="row-wrap gap-3">
            <h2 id="hb-status-title" className="card-title">Check-ins</h2>
            <Badge tone={state.tone} dot pulse={mode === 'live'} size="lg">{state.label}</Badge>
          </div>
          <p className="hb-status-lede">{state.text}</p>
          <p className="text-sm text-muted m-0">{runLine}</p>
        </div>
        <div className="hb-status-switches">
          {['enabled', 'dry_run'].map((key) => (
            <div key={key} className="setting-row">
              <div className="min-w-0">
                <label className="setting-row-title" htmlFor={`hb-${key}`}>{fields[key]?.label}</label>
                <div className="setting-row-desc" id={`hb-${key}-desc`}>{fields[key]?.help}</div>
              </div>
              <Switch id={`hb-${key}`} checked={!!settings[key]} disabled={!isSuper || !!busy}
                      onChange={(v) => saveSwitch(key, v)} aria-describedby={`hb-${key}-desc`} />
            </div>
          ))}
          {!isSuper && <p className="form-hint mb-0">Only a superadmin can change these.</p>}
        </div>
      </section>

      <div className="hb-tabs">
        <Tabs variant="line" label="Heartbeat views" idPrefix="hb" value={tab} onChange={setTab} tabs={tabs} />
      </div>

      <div role="tabpanel" id={`hb-panel-${tab}`} aria-labelledby={`hb-tab-${tab}`}>
        {tab === 'overview' && (
          <OverviewPanel overview={overview} users={users} checkins={checkins} isSuper={isSuper}
                         busy={busy} preview={preview} onPreview={runPreview}
                         onClosePreview={() => setPreview(null)} onReplan={replan}
                         onOpenSettings={() => setTab('settings')} />
        )}
        {tab === 'users' && (
          <UsersPanel users={users} overview={overview} isSuper={isSuper} busy={busy} onOptOut={setOptOut} />
        )}
        {tab === 'history' && <HistoryPanel checkins={checkins} settings={settings} />}
        {tab === 'settings' && (
          <SettingsPanel form={form} set={set} fields={fields} changes={changes} problems={problems}
                         disabled={!isSuper || !!busy} isSuper={isSuper} overview={overview} users={users} />
        )}
      </div>

      {dirty && (
        <div className="save-bar" role="region" aria-label="Unsaved settings">
          <span className="save-bar-note">
            {invalid
              ? `Fix the marked ${Object.keys(problems).length === 1 ? 'setting' : 'settings'} to save.`
              : `${plural(changeCount, 'unsaved change')}.`}
          </span>
          {tab !== 'settings' && (
            <button type="button" className="btn btn-ghost" onClick={() => setTab('settings')}>Review</button>
          )}
          <button type="button" className="btn btn-secondary" onClick={discard} disabled={!!busy}>Discard</button>
          <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={!!busy || invalid}>
            {busy === 'save' ? <><span className="spinner-sm" /> Saving…</> : 'Save changes'}
          </button>
        </div>
      )}

      {confirmDialog}
    </div>
  );
}

export default Heartbeat;
